import axios, { isAxiosError, type Method } from "axios"
import { getClerkTokenProvider } from "./clerk-token-provider"
import { clerkRequest, TokenRequestError } from "./clerk-request"
import { getClientRequestId, withIdentityAssociation } from "./request-correlation"
import { endpoints } from "../api/endpoints"

// Modern API pattern interfaces
export interface EndpointOptions {
  pathParams?: Record<string, string | number>
  queryParams?: Record<string, string | number | boolean | (string | number | boolean)[]>
  body?: unknown
  headers?: Record<string, string>
  timeoutMs?: number
}

export interface EndpointResponse<T = unknown> {
  status: number
  data?: T
  error?: TokenRequestError
}

// Auth event constants
const AUTH_EVENTS = {
  UNAUTHORIZED: "auth:unauthorized",
} as const

const handleUnauthenticated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_EVENTS.UNAUTHORIZED))
  }
}

/**
 * Build URL with path and query parameters
 */
const buildUrl = (
  baseUrl: string,
  pathParams?: Record<string, string | number>,
  queryParams?: Record<string, string | number | boolean | (string | number | boolean)[]>,
): string => {
  // Validate required path parameters (only in path, not protocol/port)
  // Extract path portion after protocol and domain/port
  const pathPortion = baseUrl.split("//")[1]?.split("/").slice(1).join("/") || baseUrl
  const requiredParams = Array.from(pathPortion.matchAll(/:([A-Za-z0-9_]+)/g)).map((m) => m[1])
  const missingParams = requiredParams.filter((key) => !(pathParams && key in pathParams))
  if (missingParams.length > 0) {
    throw new Error(`Missing required path parameters: ${missingParams.join(", ")}`)
  }

  let url = baseUrl

  // Replace path parameters with encoding
  if (pathParams) {
    for (const [key, value] of Object.entries(pathParams)) {
      url = url.replace(`:${key}`, encodeURIComponent(String(value)))
    }
  }

  // Add query parameters
  if (queryParams) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(queryParams)) {
      // Skip only null and undefined values
      if (value == null) continue

      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v != null) {
            params.append(key, String(v))
          }
        })
      } else {
        params.append(key, String(value))
      }
    }

    const queryString = params.toString()
    if (queryString) {
      // Use & if URL already has query params, otherwise ?
      url += (url.includes("?") ? "&" : "?") + queryString
    }
  }

  return url
}

/**
 * Create a modern API endpoint
 */
const createEndpoint = (baseUrl: string, method: Method, authenticated = true) => {
  const call = async <T = unknown>(opts?: EndpointOptions): Promise<EndpointResponse<T>> => {
    try {
      // Build URL with path and query parameters
      const url = buildUrl(baseUrl, opts?.pathParams, opts?.queryParams)

      // Configure headers
      const headers: Record<string, string> = { ...opts?.headers }

      // Only set Content-Type for requests with body that aren't FormData
      const isFormData = typeof FormData !== "undefined" && opts?.body instanceof FormData
      const hasContentType = Object.keys(headers).some((k) => k.toLowerCase() === "content-type")

      if (opts?.body !== undefined && !isFormData && !hasContentType) {
        headers["Content-Type"] = "application/json"
      }

      const provider = getClerkTokenProvider()
      if (authenticated && !provider) throw new TokenRequestError("temporary", "CLERK_NOT_READY")
      const response =
        authenticated && provider
          ? await clerkRequest(
              provider.getToken,
              (token) => {
                const send = (associationHeaders: Record<string, string>) =>
                  axios({
                    method,
                    url,
                    headers: {
                      ...headers,
                      ...associationHeaders,
                      "Authorization": `Bearer ${token}`,
                      "X-Vivid-Client-Id": getClientRequestId(),
                    },
                    data: opts?.body,
                    timeout: opts?.timeoutMs,
                    // Keep HTTP errors as responses until the read-only 401 recovery completes.
                    validateStatus: () => true,
                  })
                return baseUrl === endpoints.users.accessContext ||
                  baseUrl === endpoints.users.permissions
                  ? withIdentityAssociation(provider.sessionId, send)
                  : send({})
              },
              method,
              () => {
                const current = getClerkTokenProvider()
                return (
                  current !== null &&
                  current.userId === provider.userId &&
                  current.sessionId === provider.sessionId
                )
              },
            )
          : await axios({
              method,
              url,
              headers,
              data: opts?.body,
              timeout: opts?.timeoutMs,
              validateStatus: () => true,
            })
      if (authenticated && response.status === 401) handleUnauthenticated()

      return {
        status: response.status,
        data: response.data as T,
      }
    } catch (error) {
      if (error instanceof TokenRequestError) {
        return { status: error.status, data: error.data as T, error }
      }

      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          handleUnauthenticated()
        }
        return {
          status: error.response?.status ?? 0,
          data: error.response?.data as T,
        }
      }

      return {
        status: 0,
      }
    }
  }

  return { call }
}

export default createEndpoint

/**
 * Create a TanStack Query-optimized endpoint
 */
export const createTanstackEndpoint = (baseUrl: string, method: Method) => {
  const { call } = createEndpoint(baseUrl, method)

  return {
    // For useQuery - returns data directly
    query: async <T = unknown>(opts?: EndpointOptions): Promise<T> => {
      const response = await call<T>(opts)

      // Throw on non-2xx so React Query treats as error
      if (response.error) throw response.error
      if (response.status < 200 || response.status >= 300) {
        throw Object.assign(new Error(`HTTP ${response.status}`), {
          status: response.status,
          data: response.data,
        })
      }

      return response.data as T
    },

    // For useMutation - returns full response
    mutate: async <T = unknown>(opts?: EndpointOptions): Promise<EndpointResponse<T>> => {
      return call<T>(opts)
    },

    // Also expose call for direct usage
    call,
  }
}
